import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import classNames from 'classnames';
/* Components */
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';
/* Utils */
import { getLeadUid } from 'site-modules/shared/components/lead-form/utils/utils';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';

import './card.scss';

export function DealerVinCard({
  leadTarget,
  priceStrikethrough = false,
  dealerNameClasses = '',
  vinClasses = '',
  priceCopyClasses = '',
  priceValueClasses = '',
  showLeasePayments = false,
  showEmptyRating = true,
  vehicleLabel = '',
  isMainCard = false,
}) {
  const { dealer, inventory, leadAttributes } = leadTarget;

  const distance = get(leadAttributes, 'distance');
  const dealerInfo = get(inventory, 'dealerInfo', dealer);
  const vin = get(inventory, 'vin');

  const salesOverallRating = get(dealerInfo, 'displayInfo.salesOverallRating', get(dealerInfo, 'salesRating', 0));
  const salesReviewCount = get(dealerInfo, 'displayInfo.salesReviewCount', get(dealerInfo, 'salesReviewCount', 0));
  const displayPrice = get(inventory, 'prices.displayPrice');

  const uid = getLeadUid(leadTarget);
  const isUsedVehicle = isUsed(get(inventory, 'type', ''));
  const showPrice = !!displayPrice && (isUsedVehicle || priceStrikethrough);
  const monthlyPayment = get(inventory, 'prices.estimateLeasePromise.monthlyPayment');
  const showMonthlyPayment = showLeasePayments && !!monthlyPayment;
  const newLabel = showMonthlyPayment ? 'Est. Lease' : 'MSRP';
  const showRating = salesOverallRating > 0 || showEmptyRating;
  const downPayment = get(leadTarget, 'inventory.prices.estimateLeasePromise.downPayment', 0);
  const term = get(leadTarget, 'inventory.prices.estimateLeasePromise.term', 0);

  return (
    <div className="d-flex justify-content-between align-items-baseline w-100">
      <div>
        {!!vehicleLabel && <h3 className="text-primary-darker fw-bold display-lg mb-0_5">{vehicleLabel}</h3>}
        <h4 className={classNames('text-gray-darker mb-0_5', dealerNameClasses)} id={uid}>
          {dealerInfo.name}
        </h4>
        {!!vin && (
          <div className={classNames('text-gray-dark small mb-0_5', vinClasses)}>
            VIN: {vin}
            {isMainCard && showLeasePayments && `, ${term} month term, ${formatPriceString(downPayment)} down`}
          </div>
        )}
        <div className="small">
          {showRating && <RatingStars rating={salesOverallRating} className="text-primary-darker me-0_25" />}
          {showRating && <span className="text-gray-darker">({salesReviewCount})</span>}
          {distance > 0 && (
            <span className={classNames('text-gray-darker', { 'ms-1': showRating })}>
              <span className="icon-location me-0_25" aria-hidden />
              {distance.toFixed(1)} mi.
            </span>
          )}
        </div>
      </div>
      {showPrice && (
        <div className="text-end">
          <div className={classNames('xsmall text-gray-dark ', priceCopyClasses)}>
            {isUsedVehicle ? 'List' : newLabel}
          </div>
          {isUsedVehicle ? (
            <span className="text-gray-darker">{formatPriceString(displayPrice)}</span>
          ) : (
            <Fragment>
              {showMonthlyPayment ? (
                <span className={classNames('text-gray-darker', priceValueClasses)}>
                  {formatPriceString(`${Math.round(monthlyPayment)}/mo.`)}
                </span>
              ) : (
                <Fragment>
                  <span className="visually-hidden">Price Reduced From</span>
                  <s className={classNames('text-gray-darker', priceValueClasses)}>{formatPriceString(displayPrice)}</s>
                </Fragment>
              )}
            </Fragment>
          )}
        </div>
      )}
    </div>
  );
}

DealerVinCard.propTypes = {
  leadTarget: PropTypes.shape({
    inventory: PropTypes.shape({}),
    dealer: PropTypes.shape({}),
    leadAttributes: PropTypes.shape({
      primary: PropTypes.bool.isRequired,
      selected: PropTypes.bool.isRequired,
      distance: PropTypes.number,
    }).isRequired,
  }).isRequired,
  priceStrikethrough: PropTypes.bool,
  dealerNameClasses: PropTypes.string,
  vinClasses: PropTypes.string,
  priceCopyClasses: PropTypes.string,
  priceValueClasses: PropTypes.string,
  showLeasePayments: PropTypes.bool,
  showEmptyRating: PropTypes.bool,
  vehicleLabel: PropTypes.string,
  isMainCard: PropTypes.bool,
};
